import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { ja, enUS } from 'date-fns/locale';
import { AppDispatch, RootState } from '../store';
import { toggleRecord, fetchWeeklyView } from '../store/slices/recordsSlice';
import { db } from '../utils/db';
import { useTranslation } from '../hooks/useTranslation';
import { playSuccessSound } from '../assets/sounds/success-sound';
import { badgeService } from '../services/badgeService';
import { calculateGlobalStats } from '../utils/habitUtils';
import BadgeUnlockAnimation from './BadgeUnlockAnimation';
import { WeeklyHabitData } from '../store/slices/recordsSlice';
import '../styles/MonthlyView.css';

interface MonthlyViewProps {
  habitId: string;
  habitName: string;
  habitIcon: string;
  habitColor: string;
  records: any[]; // この records は MainView から渡される weeklyRecords
  onClose: () => void;
}

const MonthlyView: React.FC<MonthlyViewProps> = ({ 
  habitId, 
  habitName, 
  habitIcon, 
  habitColor, 
  records: weeklyRecords, 
  onClose 
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [monthRecords, setMonthRecords] = useState<Map<string, {date: string, status: string, note?: string}>>(new Map());
  const [unlockedBadge, setUnlockedBadge] = useState<any>(null);
  const { t, language } = useTranslation();
  const { soundEnabled } = useSelector((state: RootState) => state.ui);

  // 月が変更されたら、IndexedDB からその月のデータを取得
  useEffect(() => {
    const loadMonthRecords = async () => {
      const start = startOfMonth(currentMonth);
      const end = endOfMonth(currentMonth);
      const startStr = format(start, 'yyyy-MM-dd');
      const endStr = format(end, 'yyyy-MM-dd');
      
      const dbRecords = await db.records
        .where('habitUuid')
        .equals(habitId)
        .and(r => r.date >= startStr && r.date <= endStr)
        .toArray();
      
      const recordsMap = new Map();
      dbRecords.forEach(record => {
        recordsMap.set(record.date, {
          date: record.date,
          status: record.status,
          note: record.note
        });
      });
      
      setMonthRecords(recordsMap);
    };
    
    loadMonthRecords();
  }, [currentMonth, habitId, weeklyRecords]);

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    return eachDayOfInterval({ start, end });
  };

  const getEmptyDaysAtStart = () => {
    const firstDay = startOfMonth(currentMonth);
    const dayOfWeek = getDay(firstDay);
    return Array(dayOfWeek).fill(null);
  };

  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'completed': return '✓';
      case 'skipped': return '→';
      case 'failed': return '✗';
      default: return '・';
    }
  };

  const getStatusClass = (status: string | null) => {
    switch (status) {
      case 'completed': return 'day-completed';
      case 'skipped': return 'day-skipped';
      case 'failed': return 'day-failed';
      default: return 'day-empty';
    }
  };

  const handlePrevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const handleDayClick = async (e: React.MouseEvent, dateStr: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    const today = format(new Date(), 'yyyy-MM-dd');
    if (dateStr > today) return;

    // 現在の状態を確認（演出判定用）
    const record = monthRecords.get(dateStr);
    const isBecomingCompleted = !record || record.status !== 'completed';

    const result = await dispatch(toggleRecord({ habitId, date: dateStr })).unwrap();
    const newStatus = result.status;
    
    // 即座にローカル状態を仮更新（レスポンスを待たずに反映させるため）
    const updatedMap = new Map(monthRecords);
    if (newStatus === 'pending') {
      updatedMap.delete(dateStr);
    } else {
      updatedMap.set(dateStr, { date: dateStr, status: newStatus });
    }
    setMonthRecords(updatedMap);

    // 音を再生
    if (soundEnabled && isBecomingCompleted && newStatus === 'completed') {
      playSuccessSound(0.5).catch(() => {});
      
      try {
        // 統計の更新とバッジチェック
        const globalStats = await calculateGlobalStats();
        const currentStats = badgeService.getStats();
        const updatedStats = { ...currentStats, ...globalStats };
        
        await badgeService.updateStats(updatedStats);
        const newBadges = await badgeService.checkBadgeProgress(updatedStats);
        
        if (newBadges.length > 0) {
          setUnlockedBadge(newBadges[0]);
        }
      } catch (error) {
        console.error('[MonthlyView] Stats update failed:', error);
      }
    }
    
    // 背面の週ビューもバックグラウンドで更新
    try {
      await dispatch(fetchWeeklyView()).unwrap();
    } catch (error) {
      console.warn('[MonthlyView] Failed to refresh background view:', error);
    }
  };

  const calculateMonthlyStats = () => {
    const daysInterval = getDaysInMonth();
    let completed = 0;
    daysInterval.forEach((day: Date) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const record = monthRecords.get(dateStr);
      if (record?.status === 'completed') completed++;
    });

    const rate = daysInterval.length > 0 ? Math.round((completed / daysInterval.length) * 100) : 0;
    return { completed, rate };
  };

  const stats = calculateMonthlyStats();
  const emptyDays = getEmptyDaysAtStart();
  const days = getDaysInMonth();

  return (
    <div className="monthly-view-overlay" onClick={onClose}>
      <div className="monthly-view-modal" onClick={(e) => e.stopPropagation()}>
        <div className="monthly-header">
          <div className="monthly-title">
            <span className="monthly-icon">{habitIcon}</span>
            <span className="monthly-name">{habitName}</span>
          </div>
          <button className="monthly-close" onClick={onClose}>×</button>
        </div>

        <div className="monthly-navigation">
          <button onClick={handlePrevMonth}>◀</button>
          <h3>{format(currentMonth, language === 'ja' ? 'yyyy年 M月' : 'MMMM yyyy', { locale: language === 'ja' ? ja : enUS })}</h3>
          <button onClick={handleNextMonth}>▶</button>
        </div>

        <div className="monthly-stats">
          <div className="stat-item">
            <span className="stat-label">{t('calendar.achievementRate')}</span>
            <span className="stat-value">{stats.rate}%</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{t('calendar.completed')}</span>
            <span className="stat-value stat-completed">{stats.completed}{t('calendar.days')}</span>
          </div>
        </div>

        <div className="monthly-calendar">
          <div className="weekday-headers">
            {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map(dayKey => (
              <div key={dayKey} className="weekday-header">{t(`weekday.${dayKey}`)}</div>
            ))}
          </div>
          <div className="calendar-grid">
            {emptyDays.map((_, index) => (
              <div key={`empty-${index}`} className="calendar-day empty"></div>
            ))}
            {days.map((day: Date) => {
              const dateStr = format(day, 'yyyy-MM-dd');
              const record = monthRecords.get(dateStr);
              const status = record?.status || 'pending';
              const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');

              return (
                <button
                  key={dateStr}
                  className={`calendar-day ${getStatusClass(status)} ${isToday ? 'is-today' : ''}`}
                  onClick={(e) => handleDayClick(e, dateStr)}
                  style={{ backgroundColor: status === 'completed' ? habitColor : undefined }}
                >
                  <span className="day-number">{format(day, 'd')}</span>
                  <span className="day-status">{getStatusIcon(status)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      {unlockedBadge && (
        <BadgeUnlockAnimation 
          badge={unlockedBadge} 
          onComplete={() => setUnlockedBadge(null)} 
        />
      )}
    </div>
  );
};

export default MonthlyView;
